import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';
import { useMeQuery } from './engagement-lifecycle';
import {
  addHealthDomain,
  patchHealthDomain,
  CommitAssessmentResultSchema,
  StartAssessmentResultSchema,
  ratingToPostureStatus,
  type DomainRating,
} from './health-domains';
import { rollupColorToPostureStatus, type RollupColor } from './health-posture';
import type { PostureStatus } from '@/components/ui/posture-chip';

/**
 * Health & Readiness composite read + the rail's adjust mutations (T-0222 /
 * E20-F02).
 *
 *   GET /api/v1/engagements/:id/health-readiness   (T-0220, design §2)
 *     → { verdict, domains[], criteria[] (grouped by category),
 *         evidence: { open_findings, interview_notes },
 *         reports: { latest_report, snapshots: { count, last_at } },
 *         planned_go_live, modules: { health_check, go_live_readiness } }
 *     verdict = { posture: { color, counts }, readiness: { total_percent,
 *                 recommendation, thresholds } | null }
 *
 * One request feeds the header, the Overview and the tab lists. Every number in it is
 * one of the existing pure derivers (`deriveRollupColor`,
 * `deriveScorecardRollup`); nothing here recomputes a rollup (R6). The
 * schema is tolerant of the fields the composite may or may not carry per
 * row (rated-by names, the AI proposal, the per-criterion trend) so the two
 * tickets built in parallel meet in the middle: a field the API does not
 * send renders as its empty state, never as a schema error.
 *
 * `toHealthReadinessView` turns the wire shape into the one the screen
 * reads: posture statuses in the chip vocabulary, criteria in category
 * groups whether the API sent them flat (ordered by category) or already
 * grouped (`{ category, items }`), counts defaulted to 0.
 *
 * T-0240 (E22-design §2) adds what the charts read, all computed by the API:
 * `trend[]` (the verdicts at each readiness snapshot), `domains[].history`
 * (each domain's rating at those snapshots), `criteria[].trend[].snapshot_id`
 * (null scores where a snapshot did not hold the criterion),
 * `evidence.open_by_domain` (open findings by domain and severity),
 * `project_start`, `ai_pending` and `created_at` on the AI readings. All
 * optional on the wire, so an older API renders empty charts, not errors.
 *
 * T-0242 adds the Overview's two remaining numbers, also computed by the API:
 * `evidence.open_by_severity` (every open finding by severity) and
 * `trend_change` (the weighted total's change across the trend).
 *
 * Adjust paths (leads):
 *   - add / retire / restore a domain → the existing POST …/health/domains
 *     and PATCH …/health/domains/:id (T-0070 / T-0126), wrapped here so a
 *     change also refreshes the composite read.
 *   - criteria and weights → PATCH …/readiness/rubric with
 *     `{ criteria: [{ id?, name, category, weight, retire? }] }` (T-0219).
 *     Weights are fractions that must sum to 1; the API refuses a bad set
 *     with 400 naming the offending criteria, which `rubricSumOffenders`
 *     pulls out of the error details for the screen to show.
 *
 * Rating a domain goes through the existing assessment lifecycle (T-0072):
 * start a round when none is in progress, then commit it with the rating,
 * the reason and, for a confirm, the AI proposal id.
 */

// ===========================================================================
// Wire schema (tolerant)
// ===========================================================================

const RollupColorSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey', 'unassessed']);
const DomainRatingSchema = z.enum(['healthy', 'at_risk', 'critical']);
// `conditional` is tolerated as an alias of `conditional_go`.
const RecommendationSchema = z.enum(['go', 'conditional_go', 'no_go', 'conditional']);
const ScoreValueSchema = z.union([z.number(), z.string()]);

export const HealthReadinessPostureSchema = z
  .object({
    color: RollupColorSchema.nullable().optional(),
    counts: z
      .object({
        healthy: z.number().optional(),
        at_risk: z.number().optional(),
        critical: z.number().optional(),
        unassessed: z.number().optional(),
        total: z.number().optional(),
      })
      .passthrough(),
    last_rated_at: z.string().nullable().optional(),
  })
  .passthrough();

export const HealthReadinessReadinessSchema = z
  .object({
    total_percent: z.number().nullable(),
    recommendation: RecommendationSchema.nullable(),
    thresholds: z.object({ go: z.number(), conditional: z.number() }),
    scored_count: z.number().optional(),
    criteria_count: z.number().optional(),
    computed_at: z.string().optional(),
  })
  .passthrough();

export const HealthReadinessVerdictSchema = z.object({
  posture: HealthReadinessPostureSchema,
  readiness: HealthReadinessReadinessSchema.nullable(),
});

const ProposalCitationSchema = z
  .object({
    id: z.string(),
    doc_label: z.string(),
    section: z.string().nullable().optional(),
    page: z.number().nullable().optional(),
    confidence: z.number().optional(),
  })
  .passthrough();

export const HealthReadinessDomainProposalSchema = z
  .object({
    proposal_id: z.string().nullable().optional(),
    rating: DomainRatingSchema,
    confidence: z.number(),
    reasoning: z.string().nullable().optional(),
    citations: z.array(ProposalCitationSchema).optional(),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();

/** T-0240: a domain's rating at one readiness snapshot (null = not rated). */
export const HealthReadinessDomainHistoryPointSchema = z
  .object({
    snapshot_id: z.string(),
    captured_at: z.string(),
    posture: DomainRatingSchema.nullable(),
  })
  .passthrough();

export const HealthReadinessDomainSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    display_order: z.number().optional(),
    rating: DomainRatingSchema.nullable().optional(),
    rollup_color: RollupColorSchema.nullable().optional(),
    rated_at: z.string().nullable().optional(),
    rated_by: z.string().nullable().optional(),
    rationale: z.string().nullable().optional(),
    open_findings: z.number().optional(),
    latest_assessment_id: z.string().nullable().optional(),
    in_progress_assessment_id: z.string().nullable().optional(),
    ai_proposal: HealthReadinessDomainProposalSchema.nullable().optional(),
    read_by_criteria: z.array(z.string()).optional(),
    history: z.array(HealthReadinessDomainHistoryPointSchema).optional(),
  })
  .passthrough();

export const HealthReadinessRetiredDomainSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    retired_at: z.string().nullable().optional(),
    retired_by: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
  })
  .passthrough();

/** T-0238: a record the criterion judge suggests linking as evidence. */
const SuggestedLinkSchema = z
  .object({
    source_module: z.string(),
    source_entity_kind: z.string(),
    source_entity_id: z.string(),
    label: z.string().nullable().optional(),
  })
  .passthrough();

export const HealthReadinessCriterionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    /** Fraction of the total (0..1), as the rubric stores it. */
    weight: z.number(),
    display_order: z.number().optional(),
    score_type: z.enum(['numeric_0_10', 'pass_fail', 'ryg']).optional(),
    score: ScoreValueSchema.nullable().optional(),
    /** The committed score normalised to 0..100 by the scorecard deriver. */
    score_percent: z.number().nullable().optional(),
    /** Points this criterion adds to the weighted total (0..100 scale). */
    weighted_contribution: z.number().nullable().optional(),
    score_id: z.string().nullable().optional(),
    scored_at: z.string().nullable().optional(),
    scored_by: z.string().nullable().optional(),
    ai_candidate: z
      .object({
        proposal_id: z.string().nullable().optional(),
        score: ScoreValueSchema,
        score_percent: z.number().nullable().optional(),
        confidence: z.number(),
        created_at: z.string().nullable().optional(),
        /** T-0238: the criterion judge's reasoning, citations and suggested links. */
        reasoning: z.string().nullable().optional(),
        citations: z.array(ProposalCitationSchema).optional(),
        suggested_links: z.array(SuggestedLinkSchema).optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    evidence_count: z.number().optional(),
    needs_evidence: z.boolean().optional(),
    rubric_text: z.string().nullable().optional(),
    reads_domain_ids: z.array(z.string()).optional(),
    trend: z
      .array(
        z.object({
          snapshot_id: z.string().optional(),
          captured_at: z.string(),
          score_percent: z.number().nullable(),
        }),
      )
      .optional(),
    /** T-0240: latest value minus the first snapshot value, in points; null
     *  with fewer than two values. */
    trend_change: z.number().nullable().optional(),
  })
  .passthrough();

export const HealthReadinessCriterionGroupSchema = z
  .object({
    category: z.string(),
    weight: z.number().optional(),
    aggregate_percent: z.number().nullable().optional(),
    items: z.array(HealthReadinessCriterionSchema),
  })
  .passthrough();

/** T-0240: open findings of one domain by severity (domain_id null = no domain). */
export const HealthReadinessOpenByDomainSchema = z
  .object({
    domain_id: z.string().nullable(),
    critical: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number(),
    info: z.number().optional(),
    /** The row's length: the sum of its severities, from the API. */
    total: z.number().optional(),
  })
  .passthrough();

export const HealthReadinessEvidenceSchema = z
  .object({
    open_findings: z.number().optional(),
    resolved_findings: z.number().optional(),
    interview_notes: z.number().optional(),
    proposed_findings: z.number().optional(),
    open_by_domain: z.array(HealthReadinessOpenByDomainSchema).optional(),
    /** T-0242: every open finding by severity, from the API. */
    open_by_severity: z
      .object({
        critical: z.number(),
        high: z.number(),
        medium: z.number(),
        low: z.number(),
        info: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

/** T-0240: the verdicts at one readiness snapshot, as it froze them. */
export const HealthReadinessTrendPointSchema = z
  .object({
    snapshot_id: z.string(),
    captured_at: z.string(),
    total_percent: z.number().nullable(),
    recommendation: RecommendationSchema.nullable(),
    // The live posture's shape without last_rated_at.
    posture: z
      .object({
        color: RollupColorSchema.nullable().optional(),
        counts: HealthReadinessPostureSchema.shape.counts,
      })
      .passthrough(),
    captured_by_name: z.string().nullable().optional(),
  })
  .passthrough();

export const HealthReadinessReportSchema = z
  .object({
    report_id: z.string(),
    status: z.string(),
    cycle_label: z.string().nullable().optional(),
    drafted_at: z.string().nullable().optional(),
    published_at: z.string().nullable().optional(),
  })
  .passthrough();

export const HealthReadinessReportsSchema = z
  .object({
    latest_report: HealthReadinessReportSchema.nullable().optional(),
    snapshots: z.object({ count: z.number(), last_at: z.string().nullable() }),
  })
  .passthrough();

export const HealthReadinessSchema = z
  .object({
    verdict: HealthReadinessVerdictSchema,
    domains: z.array(HealthReadinessDomainSchema),
    retired_domains: z.array(HealthReadinessRetiredDomainSchema).optional(),
    criteria: z
      .array(z.union([HealthReadinessCriterionGroupSchema, HealthReadinessCriterionSchema]))
      .optional(),
    trend: z.array(HealthReadinessTrendPointSchema).optional(),
    /** T-0242: the weighted total's change across the trend, in points. */
    trend_change: z.number().nullable().optional(),
    evidence: HealthReadinessEvidenceSchema,
    reports: HealthReadinessReportsSchema,
    planned_go_live: z.string().nullable().optional(),
    project_start: z.string().nullable().optional(),
    ai_pending: z.object({ domains: z.number(), criteria: z.number() }).passthrough().optional(),
    modules: z.object({ health_check: z.boolean(), go_live_readiness: z.boolean() }),
  })
  .passthrough();
export type HealthReadiness = z.infer<typeof HealthReadinessSchema>;
export type HealthReadinessCriterionWire = z.infer<typeof HealthReadinessCriterionSchema>;

// ===========================================================================
// The view the screen reads
// ===========================================================================

export type Recommendation = 'go' | 'conditional_go' | 'no_go';

export interface HrDomainProposal {
  proposalId: string | null;
  rating: DomainRating;
  status: PostureStatus;
  confidence: number;
  reasoning: string | null;
  createdAt: string | null;
  citations: Array<{
    id: string;
    doc_label: string;
    section: string | null;
    page: number | null;
    confidence: number;
  }>;
}

export interface HrDomain {
  id: string;
  name: string;
  displayOrder: number;
  rating: DomainRating | null;
  status: PostureStatus;
  ratedAt: string | null;
  ratedBy: string | null;
  rationale: string | null;
  openFindings: number;
  latestAssessmentId: string | null;
  inProgressAssessmentId: string | null;
  aiProposal: HrDomainProposal | null;
  readByCriteria: string[];
  /** T-0240: the rating at each trend snapshot, oldest first. */
  history: Array<{
    snapshotId: string;
    capturedAt: string;
    rating: DomainRating | null;
    status: PostureStatus;
  }>;
}

export interface HrRetiredDomain {
  id: string;
  name: string;
  retiredAt: string | null;
  retiredBy: string | null;
  reason: string | null;
}

export interface HrSuggestedLink {
  sourceModule: string;
  sourceEntityKind: string;
  sourceEntityId: string;
  label: string | null;
}

export interface HrCriterionCandidate {
  proposalId: string | null;
  score: number | string;
  scorePercent: number | null;
  confidence: number;
  createdAt: string | null;
  /** T-0238: why the AI reads it so, in one paragraph. */
  reasoning: string | null;
  citations: Array<{ id: string; doc_label: string; section: string | null; page: number | null }>;
  /** Records it suggests linking; the reviewer keeps or drops each on confirm. */
  suggestedLinks: HrSuggestedLink[];
}

export interface HrCriterion {
  id: string;
  name: string;
  category: string;
  /** Fraction of the total (0..1). */
  weight: number;
  displayOrder: number;
  scoreType: 'numeric_0_10' | 'pass_fail' | 'ryg';
  score: number | string | null;
  scorePercent: number | null;
  weightedContribution: number | null;
  scoreId: string | null;
  scoredAt: string | null;
  scoredBy: string | null;
  aiCandidate: HrCriterionCandidate | null;
  evidenceCount: number | null;
  needsEvidence: boolean;
  rubricText: string | null;
  readsDomainIds: string[];
  trend: Array<{ snapshot_id: string | null; captured_at: string; score_percent: number | null }>;
  /** T-0240: points gained (or lost) across the trend; null when unknown. */
  trendChange: number | null;
}

export interface HrCriterionGroup {
  category: string;
  criteria: HrCriterion[];
  /** The category's share of the total as a fraction (sum of its weights),
   *  from the API; null when the API sent the criteria flat. */
  weight: number | null;
  /** Points the category earns toward the total (0..100 scale), from the
   *  API; null when none contributes. */
  aggregatePercent: number | null;
}

export interface HrVerdict {
  posture: {
    status: PostureStatus;
    counts: {
      healthy: number;
      atRisk: number;
      critical: number;
      unassessed: number;
      total: number;
    };
    lastRatedAt: string | null;
  };
  readiness: {
    totalPercent: number | null;
    recommendation: Recommendation | null;
    thresholds: { go: number; conditional: number };
    scoredCount: number;
    criteriaCount: number;
  } | null;
}

export interface HrPostureCounts {
  healthy: number;
  atRisk: number;
  critical: number;
  unassessed: number;
  total: number;
}

/** T-0240: the verdicts at one readiness snapshot. */
export interface HrTrendPoint {
  snapshotId: string;
  capturedAt: string;
  totalPercent: number | null;
  recommendation: Recommendation | null;
  posture: { status: PostureStatus; counts: HrPostureCounts };
  capturedByName: string | null;
}

/** Open findings by severity (T-0242), from the API. */
export interface HrSeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/** T-0240: open findings of one domain by severity. */
export interface HrOpenByDomain {
  domainId: string | null;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  /** The API's row total; null from an API that does not send it (the
   *  browser does not add the severities up). */
  total: number | null;
}

export interface HrLatestReport {
  reportId: string;
  status: string;
  cycleLabel: string | null;
  draftedAt: string | null;
  publishedAt: string | null;
}

export interface HealthReadinessView {
  verdict: HrVerdict;
  domains: HrDomain[];
  retiredDomains: HrRetiredDomain[];
  criteriaGroups: HrCriterionGroup[];
  criteria: HrCriterion[];
  /** T-0240: oldest snapshot first; [] when there are none. */
  trend: HrTrendPoint[];
  /** T-0242: the weighted total's change across the trend; null when unknown. */
  trendChange: number | null;
  evidence: {
    openFindings: number;
    resolvedFindings: number | null;
    interviewNotes: number;
    proposedFindings: number | null;
    openByDomain: HrOpenByDomain[];
    /** T-0242: null from an API that does not send it. */
    openBySeverity: HrSeverityCounts | null;
  };
  reports: {
    latestReport: HrLatestReport | null;
    snapshots: { count: number; lastAt: string | null };
  };
  plannedGoLive: string | null;
  projectStart: string | null;
  aiPending: { domains: number; criteria: number };
  modules: { healthCheck: boolean; goLiveReadiness: boolean };
}

function toRecommendation(
  r: z.infer<typeof RecommendationSchema> | null | undefined,
): Recommendation | null {
  if (!r) return null;
  if (r === 'conditional') return 'conditional_go';
  return r;
}

function toCriterion(c: HealthReadinessCriterionWire, index: number): HrCriterion {
  return {
    id: c.id,
    name: c.name,
    category: c.category,
    weight: c.weight,
    displayOrder: c.display_order ?? index,
    scoreType: c.score_type ?? 'numeric_0_10',
    score: c.score ?? null,
    scorePercent: c.score_percent ?? null,
    weightedContribution: c.weighted_contribution ?? null,
    scoreId: c.score_id ?? null,
    scoredAt: c.scored_at ?? null,
    scoredBy: c.scored_by ?? null,
    aiCandidate: c.ai_candidate
      ? {
          proposalId: c.ai_candidate.proposal_id ?? null,
          score: c.ai_candidate.score,
          scorePercent: c.ai_candidate.score_percent ?? null,
          confidence: c.ai_candidate.confidence,
          createdAt: c.ai_candidate.created_at ?? null,
          reasoning: c.ai_candidate.reasoning ?? null,
          citations: (c.ai_candidate.citations ?? []).map((cit) => ({
            id: cit.id,
            doc_label: cit.doc_label,
            section: cit.section ?? null,
            page: cit.page ?? null,
          })),
          suggestedLinks: (c.ai_candidate.suggested_links ?? []).map((l) => ({
            sourceModule: l.source_module,
            sourceEntityKind: l.source_entity_kind,
            sourceEntityId: l.source_entity_id,
            label: l.label ?? null,
          })),
        }
      : null,
    evidenceCount: c.evidence_count ?? null,
    needsEvidence: c.needs_evidence ?? (c.evidence_count !== undefined && c.evidence_count === 0),
    rubricText: c.rubric_text ?? null,
    readsDomainIds: c.reads_domain_ids ?? [],
    trend: (c.trend ?? []).map((p) => ({
      snapshot_id: p.snapshot_id ?? null,
      captured_at: p.captured_at,
      score_percent: p.score_percent,
    })),
    trendChange: c.trend_change ?? null,
  };
}

function toPostureCounts(
  counts: z.infer<typeof HealthReadinessPostureSchema>['counts'],
  fallbackTotal: number,
): HrPostureCounts {
  return {
    healthy: counts.healthy ?? 0,
    atRisk: counts.at_risk ?? 0,
    critical: counts.critical ?? 0,
    unassessed: counts.unassessed ?? 0,
    total: counts.total ?? fallbackTotal,
  };
}

/** Criteria in category groups, in the order the API listed them. */
function toCriterionGroups(wire: HealthReadiness['criteria']): HrCriterionGroup[] {
  const groups: HrCriterionGroup[] = [];
  const byCategory = new Map<string, HrCriterionGroup>();
  const push = (
    category: string,
    criterion: HrCriterion,
    totals: { weight?: number; aggregate_percent?: number | null } = {},
  ) => {
    let group = byCategory.get(category);
    if (!group) {
      group = {
        category,
        criteria: [],
        weight: totals.weight ?? null,
        aggregatePercent: totals.aggregate_percent ?? null,
      };
      byCategory.set(category, group);
      groups.push(group);
    }
    group.criteria.push(criterion);
  };
  let index = 0;
  for (const entry of wire ?? []) {
    // A passthrough union cannot be narrowed with `in`; the group form is
    // the one whose `items` is an array.
    const group = entry as z.infer<typeof HealthReadinessCriterionGroupSchema>;
    if (Array.isArray(group.items)) {
      for (const item of group.items) push(group.category, toCriterion(item, index++), group);
    } else {
      push(entry.category, toCriterion(entry as HealthReadinessCriterionWire, index++));
    }
  }
  for (const group of groups) {
    group.criteria.sort((a, b) => a.displayOrder - b.displayOrder);
  }
  return groups;
}

export function toHealthReadinessView(data: HealthReadiness): HealthReadinessView {
  const domains: HrDomain[] = data.domains
    .map((d, i) => {
      const rating = d.rating ?? null;
      const status: PostureStatus = rating
        ? ratingToPostureStatus(rating)
        : rollupColorToPostureStatus((d.rollup_color ?? null) as RollupColor | null);
      return {
        id: d.id,
        name: d.name,
        displayOrder: d.display_order ?? i,
        rating,
        status,
        ratedAt: d.rated_at ?? null,
        ratedBy: d.rated_by ?? null,
        rationale: d.rationale ?? null,
        openFindings: d.open_findings ?? 0,
        latestAssessmentId: d.latest_assessment_id ?? null,
        inProgressAssessmentId: d.in_progress_assessment_id ?? null,
        aiProposal: d.ai_proposal
          ? {
              proposalId: d.ai_proposal.proposal_id ?? null,
              rating: d.ai_proposal.rating,
              status: ratingToPostureStatus(d.ai_proposal.rating),
              confidence: d.ai_proposal.confidence,
              reasoning: d.ai_proposal.reasoning ?? null,
              createdAt: d.ai_proposal.created_at ?? null,
              citations: (d.ai_proposal.citations ?? []).map((c) => ({
                id: c.id,
                doc_label: c.doc_label,
                section: c.section ?? null,
                page: c.page ?? null,
                confidence: c.confidence ?? d.ai_proposal?.confidence ?? 0,
              })),
            }
          : null,
        readByCriteria: d.read_by_criteria ?? [],
        history: (d.history ?? []).map((h) => ({
          snapshotId: h.snapshot_id,
          capturedAt: h.captured_at,
          rating: h.posture,
          status: h.posture ? ratingToPostureStatus(h.posture) : 'unassessed',
        })),
      };
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const criteriaGroups = toCriterionGroups(data.criteria);
  const criteria = criteriaGroups.flatMap((g) => g.criteria);
  const readiness = data.verdict.readiness;
  const latest = data.reports.latest_report ?? null;
  return {
    verdict: {
      posture: {
        status: rollupColorToPostureStatus(
          (data.verdict.posture.color ?? null) as RollupColor | null,
        ),
        counts: toPostureCounts(data.verdict.posture.counts, data.domains.length),
        lastRatedAt: data.verdict.posture.last_rated_at ?? null,
      },
      readiness: readiness
        ? {
            totalPercent: readiness.total_percent,
            recommendation: toRecommendation(readiness.recommendation),
            thresholds: readiness.thresholds,
            scoredCount:
              readiness.scored_count ?? criteria.filter((c) => c.scorePercent !== null).length,
            criteriaCount: readiness.criteria_count ?? criteria.length,
          }
        : null,
    },
    domains,
    retiredDomains: (data.retired_domains ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      retiredAt: d.retired_at ?? null,
      retiredBy: d.retired_by ?? null,
      reason: d.reason ?? null,
    })),
    criteriaGroups,
    criteria,
    trendChange: data.trend_change ?? null,
    trend: (data.trend ?? []).map((t) => ({
      snapshotId: t.snapshot_id,
      capturedAt: t.captured_at,
      totalPercent: t.total_percent,
      recommendation: toRecommendation(t.recommendation),
      posture: {
        status: rollupColorToPostureStatus((t.posture.color ?? null) as RollupColor | null),
        counts: toPostureCounts(t.posture.counts, 0),
      },
      capturedByName: t.captured_by_name ?? null,
    })),
    evidence: {
      openFindings: data.evidence.open_findings ?? 0,
      resolvedFindings: data.evidence.resolved_findings ?? null,
      interviewNotes: data.evidence.interview_notes ?? 0,
      proposedFindings: data.evidence.proposed_findings ?? null,
      openByDomain: (data.evidence.open_by_domain ?? []).map((r) => ({
        domainId: r.domain_id,
        critical: r.critical,
        high: r.high,
        medium: r.medium,
        low: r.low,
        info: r.info ?? 0,
        total: r.total ?? null,
      })),
      openBySeverity: data.evidence.open_by_severity
        ? {
            critical: data.evidence.open_by_severity.critical,
            high: data.evidence.open_by_severity.high,
            medium: data.evidence.open_by_severity.medium,
            low: data.evidence.open_by_severity.low,
          }
        : null,
    },
    reports: {
      latestReport: latest
        ? {
            reportId: latest.report_id,
            status: latest.status,
            cycleLabel: latest.cycle_label ?? null,
            draftedAt: latest.drafted_at ?? null,
            publishedAt: latest.published_at ?? null,
          }
        : null,
      snapshots: { count: data.reports.snapshots.count, lastAt: data.reports.snapshots.last_at },
    },
    plannedGoLive: data.planned_go_live ?? null,
    projectStart: data.project_start ?? null,
    aiPending: {
      domains: data.ai_pending?.domains ?? 0,
      criteria: data.ai_pending?.criteria ?? 0,
    },
    modules: {
      healthCheck: data.modules.health_check,
      goLiveReadiness: data.modules.go_live_readiness,
    },
  };
}

// ===========================================================================
// Read
// ===========================================================================

export const healthReadinessQueryKey = (engagementId: string | undefined) =>
  ['health-readiness', engagementId] as const;

export function getHealthReadiness(engagementId: string): Promise<HealthReadiness> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health-readiness`,
    HealthReadinessSchema,
  );
}

/** The composite read, already normalised to the screen's view. */
export function useHealthReadinessQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: healthReadinessQueryKey(engagementId),
    queryFn: () => getHealthReadiness(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 15_000,
    select: toHealthReadinessView,
  });
}

/**
 * Refresh everything a rating, a score, a snapshot or a rail adjust can
 * move: the composite read and the module reads behind the panes.
 */
export function invalidateHealthReadiness(qc: QueryClient, engagementId: string | undefined) {
  if (!engagementId) return;
  void qc.invalidateQueries({ queryKey: healthReadinessQueryKey(engagementId) });
  void qc.invalidateQueries({ queryKey: ['health', 'domains', engagementId] });
  void qc.invalidateQueries({ queryKey: ['health', 'assessments', engagementId] });
  void qc.invalidateQueries({ queryKey: ['health', 'overall-posture', engagementId] });
  void qc.invalidateQueries({ queryKey: ['health-findings', engagementId] });
  void qc.invalidateQueries({ queryKey: ['readiness', 'rubric', engagementId] });
  void qc.invalidateQueries({ queryKey: ['readiness', 'scores', engagementId] });
  void qc.invalidateQueries({ queryKey: ['readiness', 'scorecard', engagementId] });
  void qc.invalidateQueries({ queryKey: ['readiness', 'trend', engagementId] });
  void qc.invalidateQueries({ queryKey: ['readiness', 'xref', engagementId] });
  void qc.invalidateQueries({ queryKey: ['snapshots', engagementId] });
}

export function useInvalidateHealthReadiness(engagementId: string | undefined) {
  const qc = useQueryClient();
  return () => invalidateHealthReadiness(qc, engagementId);
}

/**
 * Whether the signed-in user may adjust the rail (add or retire a domain
 * or a criterion, change a weight): the API's floor is engagement_lead,
 * which a firm admin clears too. False until `/me` has said so.
 */
export function useCanAdjustAssessment(): boolean {
  const role = useMeQuery().data?.role;
  return role === 'engagement_lead' || role === 'firm_admin';
}

// ===========================================================================
// Rate a domain: the existing assessment lifecycle (start → commit)
// ===========================================================================

export interface RateDomainInput {
  domainId: string;
  /** The open round to commit; omitted → a round is started first. */
  inProgressAssessmentId: string | null;
  rating: DomainRating;
  /** The reason, one or two sentences the report can quote. Required. */
  narrative: string;
  /** The AI proposal the rating answers, when there is one. */
  aiProposalId?: string | null;
  /**
   * 'override' is "Rate differently": the proposal is recorded as
   * overridden, with the narrative as the reason. Omitted for a confirm.
   */
  mode?: 'confirm' | 'override';
}

export async function rateDomain(engagementId: string, input: RateDomainInput) {
  const base = `/api/v1/engagements/${encodeURIComponent(engagementId)}/health`;
  let assessmentId = input.inProgressAssessmentId;
  if (!assessmentId) {
    const started = await apiClient(
      `${base}/domains/${encodeURIComponent(input.domainId)}/assessments`,
      StartAssessmentResultSchema,
      { method: 'POST', body: {} },
    );
    assessmentId = started.assessment_id;
  }
  return apiClient(
    `${base}/assessments/${encodeURIComponent(assessmentId)}/commit`,
    CommitAssessmentResultSchema,
    {
      method: 'PUT',
      body: {
        rating: input.rating,
        narrative: input.narrative,
        ...(input.aiProposalId ? { ai_proposal_id: input.aiProposalId } : {}),
        ...(input.aiProposalId && input.mode === 'override' ? { mode: 'override' } : {}),
      },
    },
  );
}

export function useRateDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RateDomainInput) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return rateDomain(engagementId, input);
    },
    onSuccess: () => invalidateHealthReadiness(qc, engagementId),
  });
}

// ===========================================================================
// Rail adjust: domains
// ===========================================================================

function newIdempotencyKey(prefix: string): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${prefix}-${Date.now()}`;
}

/** The tag the domain PATCH records with a retire or restore (v1 override blob). */
const DOMAIN_ADJUST_TAG = 'other';

export function useAddDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name }: { name: string }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return addHealthDomain(engagementId, { name }, newIdempotencyKey('domain-add'));
    },
    onSuccess: () => invalidateHealthReadiness(qc, engagementId),
  });
}

export function useRetireDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, reason }: { domainId: string; reason: string }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return patchHealthDomain({
        engagementId,
        domainId,
        body: { is_active: false, override: { tag: DOMAIN_ADJUST_TAG, rationale: reason } },
      });
    },
    onSuccess: () => invalidateHealthReadiness(qc, engagementId),
  });
}

export function useRestoreDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId }: { domainId: string }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return patchHealthDomain({
        engagementId,
        domainId,
        body: {
          is_active: true,
          override: {
            tag: DOMAIN_ADJUST_TAG,
            rationale: 'Restored from the Health & Readiness rail.',
          },
        },
      });
    },
    onSuccess: () => invalidateHealthReadiness(qc, engagementId),
  });
}

// ===========================================================================
// Rail adjust: criteria and weights (PATCH …/readiness/rubric, T-0219)
// ===========================================================================

export interface RubricCriteriaPatchItem {
  /** Omitted for a criterion added in this save. */
  id?: string;
  name: string;
  category: string;
  /** T-0238: what 100%, 50% and 0% look like; null clears it. */
  description?: string | null;
  /** Fraction of the total (0..1). The set must sum to 1. */
  weight: number;
  /** Retires the criterion (its scores stay attached for history). */
  retire?: boolean;
}

export interface PatchRubricCriteriaBody {
  criteria: RubricCriteriaPatchItem[];
}

const PatchRubricCriteriaResultSchema = z.object({}).passthrough();

export function patchRubricCriteria(
  engagementId: string,
  body: PatchRubricCriteriaBody,
): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/rubric`,
    PatchRubricCriteriaResultSchema,
    { method: 'PATCH', body },
  );
}

export function usePatchRubricCriteriaMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchRubricCriteriaBody) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return patchRubricCriteria(engagementId, body);
    },
    onSuccess: () => invalidateHealthReadiness(qc, engagementId),
  });
}

/**
 * The criteria the API named when it refused a weight set (400
 * `readiness.weights_sum_invalid`). Error details are strings across the
 * API, so the names arrive comma-joined in `criterion_names`; an array under
 * `offending_criteria` is read too. Anything else yields an empty list and
 * the screen shows the message alone.
 */
export function rubricSumOffenders(err: unknown): string[] {
  if (!(err instanceof ApiError) || !err.details) return [];
  const joined = err.details.criterion_names;
  if (typeof joined === 'string') {
    return joined
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  const raw = err.details.offending_criteria ?? null;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        const o = entry as Record<string, unknown>;
        if (typeof o.name === 'string') return o.name;
        if (typeof o.id === 'string') return o.id;
      }
      return null;
    })
    .filter((s): s is string => s !== null);
}
